import { TFunction } from 'i18next'
import {
  IParsedCV,
  IParsedExperience,
  IParsedNamed,
  IParsedOptionChoice,
  IParsedOptionChoices,
  ParsedOptionKind,
} from 'types/cvParserInterfaces'

/**
 * Name of a parsed role or industry in a language, or in any language it has one in
 *
 * @param item - Role or industry
 * @param languageId - Preferred language
 * @returns The name, or an empty string
 */
export const parsedName = (item: IParsedNamed, languageId: number): string =>
  item.translations.find(tr => tr.languageId === languageId && tr.name)?.name ??
  item.translations.find(tr => tr.name)?.name ??
  ''

/**
 * Roles or industries after editing their names in one language: a kept name keeps its item (and
 * its names in other languages), a new name becomes an item with only that language
 *
 * @param items - Current roles or industries
 * @param names - Names after editing
 * @param languageId - Language the names were edited in
 * @returns The roles or industries
 */
export const withParsedNames = <T extends IParsedNamed>(items: T[], names: string[], languageId: number): T[] =>
  names.map(name => {
    const existing = items.find(item => parsedName(item, languageId).toLowerCase() === name.toLowerCase())
    return existing ?? ({ translations: [{ name, languageId }] } as T)
  })

/**
 * Comma-separated names into a list, without empty or repeated names
 *
 * @param text - Comma-separated names
 * @returns The names
 */
export const splitNames = (text: string): string[] =>
  text
    .split(',')
    .map(name => name.trim())
    .filter((name, index, names) => name.length > 0 && names.indexOf(name) === index)

/**
 * Experience the CV states for a skill, role or industry, as text, e.g. "5 yrs, since 2018"
 *
 * @param experience - Explicit experience
 * @param t - Translation function
 * @returns The text, or an empty string
 */
export const formatParsedExperience = (experience: IParsedExperience, t: TFunction): string =>
  [
    experience.experienceYears ? t('admin.parse.result.experienceYears', { years: experience.experienceYears }) : '',
    experience.sinceYear ? t('admin.parse.result.sinceYear', { year: experience.sinceYear }) : '',
    experience.lastUsed ? t('admin.parse.result.lastUsedYear', { year: experience.lastUsed }) : '',
  ]
    .filter(Boolean)
    .join(', ')

/** A name in the CV that would create a new skill, role, industry, certificate or competence */
export interface IParsedNewOption {
  kind: ParsedOptionKind
  /** Shown name: the name in the display language */
  label: string
  /** Every name of the item (a role or industry has one per language) */
  names: string[]
}

const namesOf = (item: IParsedNamed): string[] => item.translations.map(tr => tr.name).filter(Boolean)

/**
 * The existing option the reviewer chose for one of the names
 *
 * @param cv - Parsed CV
 * @param kind - Option kind
 * @param names - The item's names
 * @returns The choice, or undefined
 */
export const parsedOptionChoice = (
  cv: IParsedCV,
  kind: ParsedOptionKind,
  names: string[]
): IParsedOptionChoice | undefined => names.map(name => cv.optionChoices?.[kind]?.[name]).find(Boolean)

/**
 * Whether saving would create a new option for the item: none of its names matched one when parsing,
 * and the reviewer hasn't chosen one. Unknown (false) for parse results without matches.
 *
 * @param cv - Parsed CV
 * @param kind - Option kind
 * @param names - The item's names
 * @returns True if the item would create an option
 */
export const isNewParsedOption = (cv: IParsedCV, kind: ParsedOptionKind, names: string[]): boolean => {
  const matches = cv.optionMatches?.[kind]
  return (
    !!matches &&
    names.length > 0 &&
    names.every(name => typeof matches[name] !== 'number') &&
    !parsedOptionChoice(cv, kind, names)
  )
}

/**
 * The CV's names that didn't match an existing option when parsing, one per kind and shown name, whether
 * or not the reviewer has chosen one for them yet
 *
 * @param cv - Parsed CV
 * @param languageId - Display language
 * @returns The names
 */
export const parsedNewOptions = (cv: IParsedCV, languageId: number): IParsedNewOption[] => {
  const items: IParsedNewOption[] = [
    ...cv.skills.map(skill => ({ kind: 'skill' as const, label: skill.name, names: [skill.name] })),
    ...cv.projects.flatMap(project =>
      project.skills.map(name => ({ kind: 'skill' as const, label: name, names: [name] }))
    ),
    ...[
      ...(cv.roles ?? []),
      ...cv.workHistories.flatMap(employer => employer.roles ?? []),
      ...cv.projects.flatMap(project => project.roles ?? []),
    ].map(item => ({ kind: 'role' as const, label: parsedName(item, languageId), names: namesOf(item) })),
    ...[...(cv.industries ?? []), ...cv.projects.flatMap(project => project.industries ?? [])].map(item => ({
      kind: 'industry' as const,
      label: parsedName(item, languageId),
      names: namesOf(item),
    })),
    ...cv.certificates.map(certificate => ({
      kind: 'certificate' as const,
      label: parsedName(certificate, languageId),
      names: namesOf(certificate),
    })),
    ...(cv.competences ?? []).map(competence => ({
      kind: 'competence' as const,
      label: parsedName(competence, languageId),
      names: namesOf(competence),
    })),
  ]

  const newOptions: IParsedNewOption[] = []
  for (const item of items) {
    const matches = cv.optionMatches?.[item.kind]
    if (!matches || !item.label || item.names.some(name => typeof matches[name] === 'number')) continue

    const same = newOptions.find(
      other => other.kind === item.kind && other.label.toLowerCase() === item.label.toLowerCase()
    )
    if (same) {
      same.names = [...new Set([...same.names, ...item.names])]
    } else {
      newOptions.push({ ...item })
    }
  }
  return newOptions
}

/**
 * Option choices after choosing an existing option for the names, or clearing the choice
 *
 * @param choices - Current choices
 * @param kind - Option kind
 * @param names - The names the choice is for
 * @param choice - The chosen option, or null to clear
 * @returns The choices
 */
export const withParsedOptionChoice = (
  choices: IParsedOptionChoices | undefined,
  kind: ParsedOptionKind,
  names: string[],
  choice: IParsedOptionChoice | null
): IParsedOptionChoices => {
  const others = Object.entries(choices?.[kind] ?? {}).filter(([name]) => !names.includes(name))
  const chosen = choice ? names.map(name => [name, choice] as const) : []
  return { ...choices, [kind]: Object.fromEntries([...others, ...chosen]) }
}
